import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { HubPerformance } from '@/hooks/useGestorPerformance';

interface HubEfficiencyChartProps {
  data: HubPerformance[];
}

const HubEfficiencyChart: React.FC<HubEfficiencyChartProps> = ({ data }) => {
  // Ordenar por taxa de conclusão decrescente
  const sortedData = [...data].sort((a, b) => b.taxa_conclusao - a.taxa_conclusao);
  
  const chartData = sortedData.map((hub) => ({
    name: hub.hub_name,
    taxa: hub.taxa_conclusao,
    agendadas: hub.inspecoes_agendadas,
    concluidas: hub.inspecoes_concluidas
  }));

  const getBarColor = (taxa: number) => {
    if (taxa >= 80) return 'hsl(var(--success))';
    if (taxa >= 50) return 'hsl(var(--warning))';
    return 'hsl(var(--destructive))';
  };

  // Altura dinâmica: 40px por hub, mínimo 180px
  const chartHeight = Math.max(chartData.length * 40, 180);
  const containerHeight = Math.min(chartHeight, 280);
  const needsScroll = chartHeight > containerHeight;

  return (
    <Card>
      <CardHeader className="py-3 pb-2">
        <CardTitle className="text-sm font-medium">Taxa de Conclusão por Hub (Mês Atual)</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {chartData.length === 0 ? (
          <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">
            Nenhum dado disponível
          </div>
        ) : (
          <ScrollArea className={needsScroll ? `h-[${containerHeight}px]` : ''} style={{ height: containerHeight }}>
            <div style={{ height: chartHeight, minHeight: 180 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={chartData} 
                  layout="vertical" 
                  margin={{ left: 0, right: 30, top: 5, bottom: 5 }}
                >
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    horizontal={true} 
                    vertical={false} 
                    stroke="hsl(var(--border))" 
                  />
                  <XAxis 
                    type="number" 
                    domain={[0, 100]} 
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} 
                    tickLine={false} 
                    axisLine={false}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={140}
                    tick={{ fontSize: 11, fill: 'hsl(var(--foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--chart-tooltip-bg))',
                      border: '1px solid hsl(var(--chart-tooltip-border))',
                      borderRadius: 'var(--chart-tooltip-radius)',
                      fontSize: '12px',
                      boxShadow: 'var(--chart-tooltip-shadow)',
                    }}
                    formatter={(value: number, name: string, props: any) => {
                      return [
                        `${value}% (${props.payload.concluidas}/${props.payload.agendadas})`,
                        'Taxa de Conclusão'
                      ];
                    }}
                    labelFormatter={(label) => label}
                  />
                  <ReferenceLine 
                    x={70} 
                    stroke="hsl(var(--muted-foreground))" 
                    strokeDasharray="5 5" 
                    strokeOpacity={0.7}
                  />
                  <Bar 
                    dataKey="taxa" 
                    radius={[0, 4, 4, 0]} 
                    maxBarSize={24}
                    minPointSize={2}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getBarColor(entry.taxa)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ScrollArea>
        )}
        <div className="flex items-center justify-center gap-4 mt-3 pt-2 border-t border-border/50">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div className="w-2 h-2 rounded-full bg-success"></div>
            <span>≥80%</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div className="w-2 h-2 rounded-full bg-warning"></div>
            <span>50-79%</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div className="w-2 h-2 rounded-full bg-destructive"></div>
            <span>&lt;50%</span>
          </div>
          <span className="text-xs text-muted-foreground ml-2">Meta: 70%</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default HubEfficiencyChart;
